/**
 * Timezone Service
 * 
 * Handles timezone lookups and conversions for trip planning
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { LocationCoordinates } from './location';

const TIMEZONE_CACHE_KEY = '@tripflow_timezone_cache';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

type CachedTimezone = {
  coordinates: LocationCoordinates;
  timezone: string;
  cachedAt: number;
};

type TimezoneCache = {
  [key: string]: CachedTimezone;
};

/**
 * Generate cache key from coordinates
 */
const generateCacheKey = (coordinates: LocationCoordinates): string => {
  return `${coordinates.latitude.toFixed(4)},${coordinates.longitude.toFixed(4)}`;
};

/**
 * Get timezone for a location using coordinates
 * Uses timezone API or reverse geocoding
 */
export const getTimezoneForLocation = async (
  coordinates: LocationCoordinates
): Promise<string | null> => {
  try {
    // Check cache first
    const cacheKey = generateCacheKey(coordinates);
    const cached = await getCachedTimezone(cacheKey);
    
    // If cached timezone is a UTC offset (like UTC+0), recalculate to get proper IANA timezone
    if (cached && cached.timezone && !cached.timezone.includes('/') && cached.timezone.startsWith('UTC')) {
      console.log('[Timezones] Cached timezone is UTC offset, recalculating for IANA timezone:', cached.timezone);
      // Don't use cached UTC offset, recalculate
    } else if (cached) {
      return cached.timezone;
    }

    // Use timezone API (timezoneapi.io or similar)
    // For now, we'll use a simple approximation based on longitude
    // In production, you'd use a proper timezone API
    const timezone = approximateTimezoneFromCoordinates(coordinates);
    
    if (__DEV__) {
      console.log('[Timezones] Calculated timezone:', {
        coordinates,
        timezone,
        cacheKey,
      });
    }

    // Cache the result
    await cacheTimezone(coordinates, timezone);

    return timezone;
  } catch (error) {
    console.error('[Timezones] Error getting timezone:', error);
    return null;
  }
};

/**
 * Major city timezone mappings
 * Maps approximate coordinates to IANA timezone names
 */
const MAJOR_CITY_TIMEZONES: Array<{
  latRange: [number, number];
  lngRange: [number, number];
  timezone: string;
}> = [
  // Europe
  { latRange: [40.7, 41.4], lngRange: [28.5, 29.6], timezone: 'Europe/Istanbul' }, // Istanbul
  { latRange: [48.5, 49.0], lngRange: [2.0, 2.5], timezone: 'Europe/Paris' }, // Paris
  { latRange: [51.0, 52.0], lngRange: [-0.5, 0.5], timezone: 'Europe/London' }, // London
  { latRange: [41.5, 42.5], lngRange: [12.0, 13.0], timezone: 'Europe/Rome' }, // Rome
  { latRange: [52.0, 53.0], lngRange: [13.0, 14.0], timezone: 'Europe/Berlin' }, // Berlin
  { latRange: [40.0, 41.0], lngRange: [-4.0, -3.0], timezone: 'Europe/Madrid' }, // Madrid
  { latRange: [50.0, 51.0], lngRange: [19.0, 20.0], timezone: 'Europe/Warsaw' }, // Warsaw
  { latRange: [59.0, 60.0], lngRange: [24.0, 25.0], timezone: 'Europe/Helsinki' }, // Helsinki
  { latRange: [55.0, 56.0], lngRange: [12.0, 13.0], timezone: 'Europe/Copenhagen' }, // Copenhagen
  { latRange: [59.0, 60.0], lngRange: [10.0, 11.0], timezone: 'Europe/Oslo' }, // Oslo
  { latRange: [59.0, 60.0], lngRange: [17.0, 18.0], timezone: 'Europe/Stockholm' }, // Stockholm
  { latRange: [38.0, 39.0], lngRange: [23.0, 24.0], timezone: 'Europe/Athens' }, // Athens
  { latRange: [50.0, 51.0], lngRange: [14.0, 15.0], timezone: 'Europe/Prague' }, // Prague
  { latRange: [47.0, 48.0], lngRange: [19.0, 20.0], timezone: 'Europe/Budapest' }, // Budapest
  { latRange: [45.0, 46.0], lngRange: [9.0, 10.0], timezone: 'Europe/Zurich' }, // Zurich
  { latRange: [48.0, 49.0], lngRange: [16.0, 17.0], timezone: 'Europe/Vienna' }, // Vienna
  { latRange: [52.0, 53.0], lngRange: [4.0, 5.0], timezone: 'Europe/Amsterdam' }, // Amsterdam
  { latRange: [50.0, 51.0], lngRange: [4.0, 5.0], timezone: 'Europe/Brussels' }, // Brussels
  
  // North America
  { latRange: [40.0, 41.0], lngRange: [-74.5, -73.5], timezone: 'America/New_York' }, // New York
  { latRange: [41.5, 42.5], lngRange: [-88.0, -87.0], timezone: 'America/Chicago' }, // Chicago
  { latRange: [34.0, 35.0], lngRange: [-118.5, -118.0], timezone: 'America/Los_Angeles' }, // Los Angeles
  { latRange: [29.0, 30.0], lngRange: [-95.5, -95.0], timezone: 'America/Chicago' }, // Houston
  { latRange: [45.0, 46.0], lngRange: [-74.0, -73.0], timezone: 'America/Toronto' }, // Montreal/Toronto
  { latRange: [49.0, 50.0], lngRange: [-123.5, -122.5], timezone: 'America/Vancouver' }, // Vancouver
  { latRange: [53.0, 54.0], lngRange: [-114.5, -112.5], timezone: 'America/Edmonton' }, // Edmonton / Calgary
  { latRange: [51.0, 52.0], lngRange: [-115.0, -113.5], timezone: 'America/Edmonton' }, // Calgary
  
  // Asia
  { latRange: [35.0, 36.0], lngRange: [139.0, 140.0], timezone: 'Asia/Tokyo' }, // Tokyo
  { latRange: [37.0, 38.0], lngRange: [126.0, 127.0], timezone: 'Asia/Seoul' }, // Seoul
  { latRange: [39.0, 40.0], lngRange: [116.0, 117.0], timezone: 'Asia/Shanghai' }, // Beijing
  { latRange: [22.0, 23.0], lngRange: [113.0, 114.0], timezone: 'Asia/Hong_Kong' }, // Hong Kong
  { latRange: [1.0, 2.0], lngRange: [103.0, 104.0], timezone: 'Asia/Singapore' }, // Singapore
  { latRange: [19.0, 20.0], lngRange: [72.0, 73.0], timezone: 'Asia/Kolkata' }, // Mumbai
  { latRange: [28.0, 29.0], lngRange: [77.0, 78.0], timezone: 'Asia/Kolkata' }, // Delhi
  { latRange: [-6.0, -5.0], lngRange: [106.0, 107.0], timezone: 'Asia/Jakarta' }, // Jakarta
  { latRange: [13.0, 14.0], lngRange: [100.0, 101.0], timezone: 'Asia/Bangkok' }, // Bangkok
  { latRange: [14.0, 15.0], lngRange: [120.0, 121.0], timezone: 'Asia/Manila' }, // Manila
  
  // Oceania
  { latRange: [-34.0, -33.0], lngRange: [150.0, 151.0], timezone: 'Australia/Sydney' }, // Sydney
  { latRange: [-38.0, -37.0], lngRange: [144.0, 145.0], timezone: 'Australia/Melbourne' }, // Melbourne
  { latRange: [-27.0, -26.0], lngRange: [152.0, 153.0], timezone: 'Australia/Brisbane' }, // Brisbane
  { latRange: [-37.0, -36.0], lngRange: [174.0, 175.0], timezone: 'Pacific/Auckland' }, // Auckland
  
  // Middle East
  { latRange: [31.0, 32.0], lngRange: [34.0, 35.0], timezone: 'Asia/Jerusalem' }, // Tel Aviv/Jerusalem
  { latRange: [25.0, 26.0], lngRange: [51.0, 52.0], timezone: 'Asia/Qatar' }, // Doha
  { latRange: [24.0, 25.0], lngRange: [54.0, 55.0], timezone: 'Asia/Dubai' }, // Dubai
  { latRange: [29.0, 30.0], lngRange: [31.0, 32.0], timezone: 'Africa/Cairo' }, // Cairo
  
  // South America
  { latRange: [-23.0, -22.0], lngRange: [-47.0, -46.0], timezone: 'America/Sao_Paulo' }, // São Paulo
  { latRange: [-35.0, -34.0], lngRange: [-59.0, -58.0], timezone: 'America/Argentina/Buenos_Aires' }, // Buenos Aires
  { latRange: [-33.0, -32.0], lngRange: [-71.0, -70.0], timezone: 'America/Santiago' }, // Santiago
  { latRange: [4.0, 5.0], lngRange: [-75.0, -74.0], timezone: 'America/Bogota' }, // Bogotá
  { latRange: [-13.0, -12.0], lngRange: [-77.0, -76.0], timezone: 'America/Lima' }, // Lima
];

/**
 * Approximate timezone from coordinates
 * First tries to match major cities, then falls back to longitude-based approximation
 */
const approximateTimezoneFromCoordinates = (
  coordinates: LocationCoordinates
): string => {
  // Try to match major cities first
  for (const city of MAJOR_CITY_TIMEZONES) {
    if (
      coordinates.latitude >= city.latRange[0] &&
      coordinates.latitude <= city.latRange[1] &&
      coordinates.longitude >= city.lngRange[0] &&
      coordinates.longitude <= city.lngRange[1]
    ) {
      return city.timezone;
    }
  }
  
  // Fallback: Simple approximation based on longitude
  // Each 15 degrees of longitude ≈ 1 hour timezone offset
  // This is less accurate but better than nothing
  const offsetHours = Math.round(coordinates.longitude / 15);
  const sign = offsetHours >= 0 ? '+' : '-';
  const hours = Math.abs(offsetHours);
  
  // Convert to approximate IANA timezone based on region
  if (coordinates.latitude > 0 && coordinates.latitude < 60) {
    if (coordinates.longitude >= -170 && coordinates.longitude < -50) {
      // North America
      if (coordinates.longitude >= -125 && coordinates.longitude < -102) {
        return 'America/Denver';
      }
      if (coordinates.longitude >= -102 && coordinates.longitude < -85) {
        return 'America/Chicago';
      }
      return 'America/New_York';
    }
    if (coordinates.longitude > -10 && coordinates.longitude < 40) {
      // Europe region
      return `Europe/Paris`; // Default to Paris timezone for Europe
    } else if (coordinates.longitude >= 40 && coordinates.longitude < 180) {
      // Asia region
      return `Asia/Tokyo`; // Default to Tokyo timezone for Asia
    }
  }
  
  // Default to UTC offset format
  return `UTC${sign}${hours}`;
};

export const getTimezoneForCoordinatesSync = (
  coordinates: LocationCoordinates,
): string => approximateTimezoneFromCoordinates(coordinates);

/**
 * Convert date to local time in specified timezone
 * Uses Intl.DateTimeFormat for proper timezone conversion
 */
export const convertToLocalTime = (
  date: Date,
  timezone: string
): Date => {
  try {
    // For IANA timezones (e.g., "Europe/Paris"), use Intl API
    if (timezone.includes('/')) {
      // Get the time in the target timezone as a string
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      
      const parts = formatter.formatToParts(date);
      const year = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
      const month = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10) - 1;
      const day = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      const second = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
      
      // Create a new date in local timezone with the converted values
      // This represents the local time in the target timezone
      return new Date(year, month, day, hour, minute, second);
    }
    
    // For UTC offsets (e.g., "UTC+5"), calculate manually
    if (timezone.startsWith('UTC')) {
      const offsetMatch = timezone.match(/UTC([+-])(\d+)/);
      if (offsetMatch) {
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const offsetMs = sign * hours * 60 * 60 * 1000;
        // Get UTC time and add offset
        const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
        return new Date(utcTime + offsetMs);
      }
    }

    // Fallback: return date as-is
    return date;
  } catch (error) {
    console.error('[Timezones] Error converting time:', error);
    return date;
  }
};

/**
 * Get cached timezone
 */
const getCachedTimezone = async (cacheKey: string): Promise<CachedTimezone | null> => {
  try {
    const cacheData = await AsyncStorage.getItem(TIMEZONE_CACHE_KEY);
    if (!cacheData) return null;

    const cache: TimezoneCache = JSON.parse(cacheData);
    const cached = cache[cacheKey];

    if (!cached) return null;

    // Check if cache is expired
    const age = Date.now() - cached.cachedAt;
    if (age > CACHE_TTL) {
      delete cache[cacheKey];
      await AsyncStorage.setItem(TIMEZONE_CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return cached;
  } catch (error) {
    console.warn('[Timezones] Error reading cache:', error);
    return null;
  }
};

/**
 * Cache timezone
 */
const cacheTimezone = async (
  coordinates: LocationCoordinates,
  timezone: string
): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(TIMEZONE_CACHE_KEY);
    const cache: TimezoneCache = cacheData ? JSON.parse(cacheData) : {};

    const cacheKey = generateCacheKey(coordinates);
    cache[cacheKey] = {
      coordinates,
      timezone,
      cachedAt: Date.now(),
    };

    await AsyncStorage.setItem(TIMEZONE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[Timezones] Error caching timezone:', error);
  }
};

/**
 * Get current time in timezone
 * Returns the current Date (which is always in UTC internally)
 * The timezone is used only for formatting, not for the Date object itself
 */
export const getCurrentTimeInTimezone = (timezone: string): Date => {
  // Date objects in JavaScript are always UTC internally
  // The timezone is only relevant when formatting for display
  return new Date();
};

/**
 * Format time in timezone
 * Supports both time and date formatting
 */
export const formatTimeInTimezone = (
  date: Date,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  try {
    // Use Intl.DateTimeFormat if timezone is a valid IANA timezone
    if (timezone.includes('/')) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        ...options,
      }).format(date);
    }

    // For UTC offsets, calculate the offset and format manually
    if (timezone.startsWith('UTC')) {
      const offsetMatch = timezone.match(/UTC([+-])(\d+)/);
      if (offsetMatch) {
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const offsetMinutes = sign * hours * 60;
        
        // Get UTC time
        const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
        // Apply offset
        const targetTime = new Date(utcTime + (offsetMinutes * 60 * 1000));
        
        // Format using default options if none provided
        const defaultOptions: Intl.DateTimeFormatOptions = {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          ...options,
        };
        
        return targetTime.toLocaleString('en-US', defaultOptions);
      }
    }

    // Fallback: use local timezone
    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('[Timezones] Error formatting time:', error);
    return date.toLocaleString('en-US', options);
  }
};

